import { useAuth } from '@/hooks/auth/useAuth';
import { orgScope, type OrgScope } from '@/lib/query-keys';

/**
 * Returns the cache-segment string used to namespace org-scoped React
 * Query keys. In normal flow this is always the active organization id —
 * the auth bootstrap resolves a membership before the first authed page
 * renders and the server `requirePerm(...)` middleware 400s any request
 * without an active org. The {@link orgScope} helper's `'global'`
 * fallback is a defensive sentinel for the brief pre-auth bootstrap
 * window only and should never appear once the user is signed in. The
 * cleanup of that fallback (replacing it with `string | undefined` plus
 * auth-ready guards on every consumer) has a ~50-call-site blast radius, so it
 * is a change of its own and is not blocking.
 *
 * Use this in every hook whose underlying server endpoint filters by the
 * `x-organization-id` header. Hard-coded global keys (e.g. user theme,
 * server status) don't need it. Queries on the host `request()` transport
 * cached long-term (`staleTime: Infinity`) should also pass the same org
 * through `RequestOptions.organizationId` so the request header can never
 * diverge from the key — see `orgHeaders` and `usePlugins`. The ui-client
 * transport (`@reflex/ui/client/http`) has no equivalent override; its
 * org-keyed consumers rely on the URL/localStorage derivation.
 *
 * Why a hook? Encoding the org id in the key, rather than relying on
 * `queryClient.invalidateQueries()` after the org changes, fixes the
 * narrow window where React Query rendered the previous org's cached
 * value before the refetch landed. Distinct keys mean distinct cache
 * slots — no flash, no cross-org leak.
 */
export function useOrgKey(): OrgScope {
  const { activeOrganizationId } = useAuth();
  return orgScope(activeOrganizationId);
}
